import {
  dataSources as stardogDataSources,
  query as stardogQuery,
  virtualGraphs as stardogVirtualGraphs,
} from 'stardog';
import { MimeType } from 'vet-bones/constants';

import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { request } from 'src/actions/request/request';
import { ActionCreatorAction, createTypedAction } from 'src/actions/types';
import { Connection } from 'src/store/connections/types';
import { SqlDataSource } from 'src/store/state/dataSources';
import { buildCatalogPrimaryKeyTableColumnsQuery } from 'src/templates/queries/catalogPrimaryKeyTableColumns';

export const dataSourcesActionCreators = {
  addDataSourceStarted: () =>
    createTypedAction(DataSourcesActionType.ADD_DATA_SOURCE_STARTED),
  addDataSourceFinished: () =>
    createTypedAction(DataSourcesActionType.ADD_DATA_SOURCE_FINISHED),
  refreshDataSourcesStarted: () =>
    createTypedAction(DataSourcesActionType.REFRESH_DATA_SOURCES_STARTED),
  refreshDataSourcesFinished: () =>
    createTypedAction(DataSourcesActionType.REFRESH_DATA_SOURCES_FINISHED),
  refreshDataSourceTablesStarted: (dataSourceId: string) =>
    createTypedAction(
      DataSourcesActionType.REFRESH_DATA_SOURCE_TABLES_STARTED,
      { dataSourceId }
    ),
  refreshDataSourceTablesFinished: (dataSourceId: string) =>
    createTypedAction(
      DataSourcesActionType.REFRESH_DATA_SOURCE_TABLES_FINISHED,
      { dataSourceId }
    ),
  noSuggestionsFound: () =>
    createTypedAction(DataSourcesActionType.NO_SUGGESTIONS_FOUND_WARNING),
};

export const dataSourcesStardogRequestDispatchers = {
  addDataSource: async (dataSource: SqlDataSource) =>
    request({
      api: stardogDataSources.add,
      args: [dataSource.id, dataSource.options],
      attempt: DataSourcesActionType.ADD_DATA_SOURCE_ATTEMPT,
      success: DataSourcesActionType.ADD_DATA_SOURCE_SUCCESS,
      failure: DataSourcesActionType.ADD_DATA_SOURCE_FAILURE,
      action: { dataSource },
    }),
  refreshDataSourceTypeMetadata: async (
    dataSourceId: string,
    connection: Connection
  ) =>
    request({
      api: stardogDataSources.refreshMetadata,
      args: [dataSourceId],
      attempt: DataSourcesActionType.REFRESH_DATA_SOURCE_METADATA_ATTEMPT,
      success: DataSourcesActionType.REFRESH_DATA_SOURCE_METADATA_SUCCESS,
      failure: DataSourcesActionType.REFRESH_DATA_SOURCE_METADATA_FAILURE,
      action: { connection },
    }),
  requestDataSourceTypeDescription: async (connection: Connection) =>
    request({
      api: stardogDataSources.typeDescription,
      args: [],
      attempt:
        DataSourcesActionType.REQUEST_DATA_SOURCE_TYPE_DESCRIPTION_ATTEMPT,
      success:
        DataSourcesActionType.REQUEST_DATA_SOURCE_TYPE_DESCRIPTION_SUCCESS,
      failure:
        DataSourcesActionType.REQUEST_DATA_SOURCE_TYPE_DESCRIPTION_FAILURE,
      action: { connection },
    }),
  requestDataSourceInfo: async (dataSourceId: string) =>
    request({
      api: stardogDataSources.info,
      args: [dataSourceId],
      attempt: DataSourcesActionType.REQUEST_DATA_SOURCE_INFO_ATTEMPT,
      success: DataSourcesActionType.REQUEST_DATA_SOURCE_INFO_SUCCESS,
      failure: DataSourcesActionType.REQUEST_DATA_SOURCE_INFO_FAILURE,
      action: { dataSourceId },
    }),
  requestDataSourceListInfo: async () =>
    request({
      api: stardogDataSources.listInfo,
      args: [],
      attempt: DataSourcesActionType.REQUEST_DATA_SOURCE_LIST_INFO_ATTEMPT,
      success: DataSourcesActionType.REQUEST_DATA_SOURCE_LIST_INFO_SUCCESS,
      failure: DataSourcesActionType.REQUEST_DATA_SOURCE_LIST_INFO_FAILURE,
      action: {},
    }),
  requestDataSourceOptions: async (
    dataSourceId: string,
    connection: Connection
  ) =>
    request({
      api: stardogDataSources.options,
      args: [dataSourceId],
      attempt: DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_ATTEMPT,
      success: DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_SUCCESS,
      failure: DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_FAILURE,
      action: { connection, dataSourceId },
    }),
  requestDataSourceTableMetadata: async (
    dataSourceId: string,
    isCatalogSupported: boolean
  ) =>
    request({
      api: stardogDataSources.getMetadata,
      args: [dataSourceId, { accept: MimeType.JSON_LD }],
      attempt: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_METADATA_ATTEMPT,
      failure: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_METADATA_FAILURE,
      success: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_METADATA_SUCCESS,
      action: { dataSourceId, isCatalogSupported },
    }),
  requestDataSourceTables: async (
    dataSourceId: string,
    isCatalogSupported: boolean
  ) =>
    request({
      api: stardogDataSources.getTables,
      args: [dataSourceId],
      attempt: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLES_ATTEMPT,
      failure: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLES_FAILURE,
      success: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLES_SUCCESS,
      action: { dataSourceId, isCatalogSupported },
    }),
  requestDataSourceTableColumns: async ({
    databaseId,
    dataSourceIds,
  }: {
    databaseId: string;
    dataSourceIds: string[];
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        buildCatalogPrimaryKeyTableColumnsQuery(dataSourceIds),
      ],
      attempt: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_COLUMNS_ATTEMPT,
      failure: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_COLUMNS_FAILURE,
      success: DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_COLUMNS_SUCCESS,
      action: { databaseId, dataSourceIds },
    }),
  requestVirtualGraphs: async () =>
    request({
      api: stardogVirtualGraphs.listInfo,
      args: [],
      attempt: DataSourcesActionType.REQUEST_VIRTUAL_GRAPHS_ATTEMPT,
      success: DataSourcesActionType.REQUEST_VIRTUAL_GRAPHS_SUCCESS,
      failure: DataSourcesActionType.REQUEST_VIRTUAL_GRAPHS_FAILURE,
      action: {},
    }),
  requestSuggestions: async ({
    connection,
    content,
  }: {
    connection?: Connection;
    content: string;
  }) =>
    request({
      api: stardogDataSources.suggestions,
      args: [content],
      attempt: DataSourcesActionType.REQUEST_SUGGESTIONS_ATTEMPT,
      success: DataSourcesActionType.REQUEST_SUGGESTIONS_SUCCESS,
      failure: DataSourcesActionType.REQUEST_SUGGESTIONS_FAILURE,
      action: { connection },
    }),
  updateDataSource: async ({
    dataSourceId,
    deltaOptions,
    force = false,
  }: {
    dataSourceId: string;
    deltaOptions: Record<string, string>;
    force?: boolean;
  }) => {
    return request({
      api: stardogDataSources.update,
      args: [dataSourceId, deltaOptions, { delta_options: true, force }],
      attempt: DataSourcesActionType.UPDATE_DATA_SOURCE_ATTEMPT,
      success: DataSourcesActionType.UPDATE_DATA_SOURCE_SUCCESS,
      failure: DataSourcesActionType.UPDATE_DATA_SOURCE_FAILURE,
      action: {
        dataSourceId,
        deltaOptions,
      },
    });
  },
};

export type DataSourcesAction = ActionCreatorAction<
  typeof dataSourcesActionCreators & typeof dataSourcesStardogRequestDispatchers
>;
